import { useState, useEffect, useRef } from 'react'
import { QrCode, Scan, Download, Eye, X, Camera, CameraOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuth } from '@/hooks/useAuth'
import QRCodeLib from 'qrcode'
import QrScanner from 'qr-scanner'

interface Customer {
  id: string
  full_name: string
  email: string
  phone?: string
  points: number
  total_visits: number
  referral_code: string
  created_at: string
}

const QRCodeManager = () => {
  const { tenantId } = useAuth()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [isQrModalOpen, setIsQrModalOpen] = useState(false)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [scanResult, setScanResult] = useState<string>('')
  const videoRef = useRef<HTMLVideoElement>(null)
  const qrScannerRef = useRef<QrScanner | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (tenantId) {
      fetchCustomers()
    }
  }, [tenantId])

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (qrScannerRef.current) {
        qrScannerRef.current.stop()
        qrScannerRef.current.destroy()
      }
    }
  }, [])

  const fetchCustomers = async () => {
    if (!tenantId) return
    
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('full_name', { ascending: true })

      if (error) throw error

      setCustomers(data || [])
      await auditLogger.logDataAccess('customers', tenantId, 'view')
    } catch (error: any) {
      console.error('Error fetching customers:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Kunden konnten nicht geladen werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const generateQRCode = async (customer: Customer) => {
    try {
      // Create QR code data with customer info and tenant context
      const qrData = JSON.stringify({
        type: 'loyalty_stamp',
        tenantId: tenantId,
        customerId: customer.id,
        customerCode: customer.referral_code,
        timestamp: Date.now()
      })

      const dataUrl = await QRCodeLib.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })

      setSelectedCustomer(customer)
      setQrCodeDataUrl(dataUrl)
      setIsQrModalOpen(true)

      await auditLogger.logAdminAction('generate_qr', 'customers', customer.id, {
        customer_name: customer.full_name
      })

    } catch (error: any) {
      console.error('Error generating QR code:', error)
      toast({
        variant: "destructive",
        title: "Fehler bei QR-Code Erstellung",
        description: "QR-Code konnte nicht generiert werden."
      })
    }
  }

  const downloadQRCode = () => {
    if (!qrCodeDataUrl || !selectedCustomer) return

    const link = document.createElement('a')
    link.download = `qr-${selectedCustomer.full_name.replace(/\s+/g, '-')}-${selectedCustomer.referral_code}.png`
    link.href = qrCodeDataUrl
    link.click()

    toast({
      title: "QR-Code heruntergeladen",
      description: `QR-Code für ${selectedCustomer.full_name} wurde heruntergeladen.`
    })
  }

  const startScanner = async () => {
    if (!videoRef.current) return

    try {
      setIsScannerOpen(true)
      setScanResult('')

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result: QrScanner.ScanResult) => {
          handleScanResult(result.data)
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true
        }
      )

      await qrScannerRef.current.start()

    } catch (error: any) {
      console.error('Error starting scanner:', error)
      toast({
        variant: "destructive",
        title: "Kamera-Fehler",
        description: "Kamera konnte nicht gestartet werden. Stellen Sie sicher, dass Sie die Kamera-Berechtigung erteilt haben."
      })
      setIsScannerOpen(false)
    }
  }

  const stopScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop()
      qrScannerRef.current.destroy()
      qrScannerRef.current = null
    }
    setIsScannerOpen(false)
    setScanResult('')
  }

  const handleScanResult = async (data: string) => {
    try {
      // Parse QR code data
      const qrData = JSON.parse(data)
      
      if (qrData.type !== 'loyalty_stamp' || qrData.tenantId !== tenantId) {
        throw new Error('Ungültiger QR-Code')
      }

      // Find customer
      const customer = customers.find(c => c.id === qrData.customerId)
      if (!customer) {
        throw new Error('Kunde nicht gefunden')
      }

      // Award stamp
      await awardStamp(customer)
      
      setScanResult(`Stempel erfolgreich vergeben an ${customer.full_name}`)
      
      // Stop scanner after successful scan
      setTimeout(() => {
        stopScanner()
      }, 2000)

    } catch (error: any) {
      console.error('Error processing scan result:', error)
      setScanResult('Fehler: Ungültiger QR-Code oder Kunde nicht gefunden')
      
      toast({
        variant: "destructive",
        title: "QR-Code Fehler",
        description: error.message || "QR-Code konnte nicht verarbeitet werden."
      })
    }
  }

  const awardStamp = async (customer: Customer) => {
    if (!tenantId) return

    try {
      // Get current customer data
      const { data: currentCustomer, error: fetchError } = await supabase
        .from('customers')
        .select('points, total_visits')
        .eq('id', customer.id)
        .single()

      if (fetchError) throw fetchError

      // Add stamp
      const { error: stampError } = await supabase
        .from('stamps')
        .insert({
          tenant_id: tenantId,
          customer_id: customer.id,
          points_awarded: 1,
          stamp_type: 'visit'
        })

      if (stampError) throw stampError

      // Update customer
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          points: currentCustomer.points + 1,
          total_visits: currentCustomer.total_visits + 1,
          last_visit: new Date().toISOString()
        })
        .eq('id', customer.id)

      if (updateError) throw updateError

      await auditLogger.logAdminAction('qr_stamp_awarded', 'stamps', customer.id, {
        customer_name: customer.full_name,
        points_awarded: 1
      })

      toast({
        title: "Stempel vergeben",
        description: `${customer.full_name} hat einen Stempel via QR-Code erhalten.`
      })

      // Refresh customer data
      fetchCustomers()

    } catch (error: any) {
      console.error('Error awarding stamp:', error)
      throw error
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('de-DE')
  }

  const getPointsBadgeColor = (points: number) => {
    if (points >= 20) return 'bg-purple-100 text-purple-800 hover:bg-purple-100'
    if (points >= 10) return 'bg-blue-100 text-blue-800 hover:bg-blue-100'
    if (points >= 5) return 'bg-green-100 text-green-800 hover:bg-green-100'
    return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">QR-Code Manager</h2>
            <p className="text-muted-foreground">
              QR-Codes generieren und scannen für das Treueprogramm
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={startScanner} 
              disabled={isScannerOpen}
              className="flex items-center gap-2"
            >
              <Scan className="h-4 w-4" />
              QR-Code scannen
            </Button>
          </div>
        </div>

        {/* QR Scanner */}
        {isScannerOpen && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    QR-Code Scanner
                  </CardTitle>
                  <CardDescription>
                    Richten Sie die Kamera auf einen QR-Code, um einen Stempel zu vergeben
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={stopScanner}>
                  <X className="h-4 w-4 mr-2" />
                  Scanner beenden
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    playsInline
                  />
                  {scanResult && (
                    <div className="absolute bottom-4 left-4 right-4 p-3 bg-black/80 text-white rounded-md">
                      {scanResult}
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Positionieren Sie den QR-Code im Kamerabereich
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer QR Codes Table */}
        <Card>
          <CardHeader>
            <CardTitle>Kunden QR-Codes ({customers.length})</CardTitle>
            <CardDescription>
              Generieren und verwalten Sie QR-Codes für Ihre Kunden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Punkte</TableHead>
                    <TableHead>Besuche</TableHead>
                    <TableHead>Registriert</TableHead>
                    <TableHead className="text-right">QR-Code</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Lade Kunden...
                      </TableCell>
                    </TableRow>
                  ) : customers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Noch keine Kunden registriert.
                      </TableCell>
                    </TableRow>
                  ) : (
                    customers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{customer.full_name}</div>
                            <div className="text-sm text-muted-foreground">{customer.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {customer.referral_code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge className={getPointsBadgeColor(customer.points)}>
                            {customer.points} Punkte
                          </Badge>
                        </TableCell>
                        <TableCell>{customer.total_visits}</TableCell>
                        <TableCell>{formatDate(customer.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => generateQRCode(customer)}
                            disabled={loading}
                          >
                            <QrCode className="h-4 w-4 mr-1" />
                            QR-Code
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* QR Code Display Modal */}
        <Dialog open={isQrModalOpen} onOpenChange={setIsQrModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>QR-Code für {selectedCustomer?.full_name}</DialogTitle>
              <DialogDescription>
                Dieser QR-Code kann gescannt werden, um einen Stempel zu vergeben
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-col items-center space-y-4 py-4">
              {qrCodeDataUrl && (
                <div className="p-4 bg-white rounded-lg border">
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code" 
                    className="w-64 h-64"
                  />
                </div>
              )}
              
              {selectedCustomer && (
                <div className="text-center space-y-1">
                  <p className="font-medium">{selectedCustomer.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Code: {selectedCustomer.referral_code}
                  </p>
                  <Badge className={getPointsBadgeColor(selectedCustomer.points)}>
                    {selectedCustomer.points} Punkte
                  </Badge>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setIsQrModalOpen(false)}>
                Schließen
              </Button>
              <Button onClick={downloadQRCode} className="w-full sm:w-auto">
                <Download className="h-4 w-4 mr-2" />
                Herunterladen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}

export default QRCodeManager